/**
 * `CarLayer` (03 §7, §10): one quad mesh + thin instances, screen-constant size in stage px
 * (× uPxScale, per-camera viewport), per-instance colour / outline / core / size / visibility, and
 * per-frame draw-order reordering. Built-in modes:
 *
 * - `'disc'` (default, unchanged): disc + outline + core, per-instance rgba packing (below).
 * - `'atlas'` (03 §10 gap 7): non-rotating atlas sprites — per-instance cell index (`setCell`)
 *   picks a `uvRect` from the style's cell table (`atlasCells` + `atlasTexture`), tint/fade via
 *   `setTint`, quad height = `sizePx` (width = height · cell aspect). `pixelSnap` defaults on and
 *   snaps the **box origin** to whole stage px (v6 `blit_sprite`, risk 9) — heading-bucket map
 *   sprites and the non-rotating SC text quads (corner names) are the same mode.
 * - `'rotor'`: continuous rotation of one hi-res sprite cell — per-instance angle (`setAngle`),
 *   trilinear mip sampling (supply a mipmapped texture), and a 3×3 shader keyline at ±`keylinePx`
 *   stage px (`keyline`/`keylineAlpha`/`keylinePx`; plan: `#0E0F12` α .88·(1 − a)). Not snapped by
 *   default (zoom-in path, fractional sizes). `premultiply` outputs premultiplied rgb (blending
 *   ONE / ONE_MINUS_SRC_ALPHA) for both sprite modes.
 *
 * Instance data rides in the 16-float thin-instance "matrix" buffer (Babylon's instancing path),
 * read in the shader as world0..world3. Disc packing:
 *   world0 = (x, y, sizePx, visible)          world2 = outline rgba (0..1)
 *   world1 = fill rgba (0..1)                 world3 = core rgba (core alpha 0 = no core)
 * Sprite (atlas/rotor) packing:
 *   world0 = (x, y, sizePx, visible)          world2 = (tintA, u0, v0, u1)
 *   world1 = (angle, tintR, tintG, tintB)     world3 = (v1, cell aspect, 0, 0)
 * Bounding-info sync is disabled and the mesh is always active (the data is not a matrix).
 *
 * Style extensions (03 §10): `CarLayerOptions.material` supplies a custom `ShaderMaterial` (used
 * as-is; the layer still registers it with the UniformHub and disposes it with the mesh — the
 * style-owned atlas texture is never disposed), and `CarLayerOptions.extra` adds named
 * per-instance float attributes (`CarLayer.extraRow`, permuted into draw order and uploaded by
 * `setCars`, allocation-free per frame).
 */
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import '@babylonjs/core/Meshes/thinInstanceMesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import { Constants } from '@babylonjs/core/Engines/constants';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Scene } from '@babylonjs/core/scene';
import type { RGB } from '../api.ts';
import { GROUP, HUB_UNIFORMS, LAYER, type UniformHub } from './engine.ts';
import { GLSL_HUB } from './shaders/common.ts';

const CAR_VERTEX = /* glsl */ `
precision highp float;
attribute vec3 position;
#ifdef INSTANCES
attribute vec4 world0;
attribute vec4 world1;
attribute vec4 world2;
attribute vec4 world3;
#endif
uniform mat4 viewProjection;
uniform float uOutlinePx;
uniform float uCorePx;
uniform float uPixelSnap;
${GLSL_HUB}
varying vec2 vLocal;
varying vec4 vFill;
varying vec4 vOutline;
varying vec4 vCore;
varying float vSize;
void main() {
#ifdef INSTANCES
  vec4 d0 = world0; vec4 d1 = world1; vec4 d2 = world2; vec4 d3 = world3;
#else
  vec4 d0 = vec4(0.0, 0.0, 10.0, 1.0); vec4 d1 = vec4(1.0); vec4 d2 = vec4(0.0, 0.0, 0.0, 1.0); vec4 d3 = vec4(0.0);
#endif
  if (d0.w < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
  vec4 c = viewProjection * vec4(d0.x, 0.0, d0.y, 1.0);
  vec2 ndc = c.xy / c.w;
  if (uPixelSnap > 0.5) {
    // centre on the stage-px grid (cell centres), cells counted from the stage top like ucStageCell()
    float n = max(1.0, floor(uPxScale + 0.5));
    vec2 px = (ndc * 0.5 + 0.5) * uViewportPx + uViewportOrigin;   // canvas backbuffer px, y up
    vec2 sp = vec2(px.x, uBackbufferPx.y - px.y);                  // y down
    sp = (floor(sp / n) + 0.5) * n;
    px = vec2(sp.x, uBackbufferPx.y - sp.y) - uViewportOrigin;
    ndc = px / uViewportPx * 2.0 - 1.0;
  }
  float halfStage = 0.5 * d0.z + 1.0;          // +1 stage px AA margin
  vec2 offPx = position.xy * halfStage * uPxScale;
  gl_Position = vec4(ndc + offPx * 2.0 / uViewportPx, 0.0, 1.0);
  vLocal = position.xy * halfStage;           // stage px from the centre
  vFill = d1; vOutline = d2; vCore = d3; vSize = d0.z;
}
`;

const CAR_FRAGMENT = /* glsl */ `
precision highp float;
uniform float uOutlinePx;
uniform float uCorePx;
varying vec2 vLocal;
varying vec4 vFill;
varying vec4 vOutline;
varying vec4 vCore;
varying float vSize;
void main() {
  float d = length(vLocal);
  float aa = max(fwidth(d), 1e-4);
  float rOuter = 0.5 * vSize;
  float aOuter = clamp((rOuter - d) / aa + 0.5, 0.0, 1.0);
  if (aOuter <= 0.0) discard;
  float tFill = clamp((rOuter - uOutlinePx - d) / aa + 0.5, 0.0, 1.0);
  vec4 col = mix(vOutline, vFill, tFill);
  float tCore = clamp((0.5 * uCorePx - d) / aa + 0.5, 0.0, 1.0) * vCore.a * step(1e-3, uCorePx);
  col.rgb = mix(col.rgb, vCore.rgb, tCore);
  gl_FragColor = vec4(col.rgb, col.a * aOuter);
}
`;

/** Stage-px origin snap for the sprite modes (v6 `blit_sprite`): the box origin lands on whole
 *  stage px — `n` backbuffer px each — and the centre follows, so odd and even sizes both land on
 *  the stage grid and u = 2 is exactly the u = 1 picture ×2 (risk 9). `sp` is canvas backbuffer px
 *  (y down), `halfBb` the sprite's half size in backbuffer px. Mirrored by `snapSpriteNdc`, which
 *  the unit tests run at u = 1 and u = 2. */
export const CAR_SPRITE_SNAP_GLSL = /* glsl */ `
vec2 halfBb = halfPx * uPxScale;
vec2 org = floor((sp - halfBb) / n + 0.5) * n;
px = org + halfBb;
`;

/**
 * Sprite vertex shader (atlas / rotor). world0 = (x, y, sizePx, visible), world1 = (angle,
 * tintR, tintG, tintB), world2 = (tintA, u0, v0, u1), world3 = (v1, aspect, 0, 0) — angle in
 * radians, 0 = atlas upright; aspect = cell width / height in texels (quad height = sizePx).
 * Exported for the unit tests (source locks: stage-px origin snap, uv packing, keyline basis).
 */
export const CAR_SPRITE_VERTEX = /* glsl */ `
precision highp float;
attribute vec3 position;
#ifdef INSTANCES
attribute vec4 world0;
attribute vec4 world1;
attribute vec4 world2;
attribute vec4 world3;
#endif
uniform mat4 viewProjection;
uniform float uPixelSnap;
${GLSL_HUB}
varying vec2 vUv;
varying vec4 vTint;
varying vec2 vUvKx;
varying vec2 vUvKy;
void main() {
#ifdef INSTANCES
  vec4 d0 = world0; vec4 d1 = world1; vec4 d2 = world2; vec4 d3 = world3;
#else
  vec4 d0 = vec4(0.0, 0.0, 10.0, 1.0);
  vec4 d1 = vec4(0.0, 1.0, 1.0, 1.0);
  vec4 d2 = vec4(1.0, 0.0, 0.0, 1.0);
  vec4 d3 = vec4(1.0, 1.0, 0.0, 0.0);
#endif
  if (d0.w < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
  float halfH = 0.5 * d0.z;
  vec2 halfPx = vec2(halfH * d3.y, halfH);
  float ca = cos(d1.x);
  float sa = sin(d1.x);
  vec2 p = position.xy * halfPx;
  vec2 scr = vec2(ca * p.x - sa * p.y, sa * p.x + ca * p.y);
  vec4 c = viewProjection * vec4(d0.x, 0.0, d0.y, 1.0);
  vec2 ndc = c.xy / c.w;
  if (uPixelSnap > 0.5) {
    // v6 blit_sprite: snap the box origin to the stage-px grid (n backbuffer px each)
    float n = max(1.0, floor(uPxScale + 0.5));
    vec2 px = (ndc * 0.5 + 0.5) * uViewportPx + uViewportOrigin;
    vec2 sp = vec2(px.x, uBackbufferPx.y - px.y);
${CAR_SPRITE_SNAP_GLSL}
    ndc = (vec2(px.x, uBackbufferPx.y - px.y) - uViewportOrigin) / uViewportPx * 2.0 - 1.0;
  }
  gl_Position = vec4(ndc + scr * uPxScale * 2.0 / uViewportPx, 0.0, 1.0);
  vec2 uv0 = d2.yz;
  vec2 uv1 = vec2(d2.w, d3.x);
  vec2 uvH = 0.5 * (uv1 - uv0);
  vUv = 0.5 * (uv0 + uv1) + position.xy * uvH;
  vTint = vec4(d1.y, d1.z, d1.w, d2.x);
  // screen px → uv basis (inverse rotation): the fragment keyline taps step ±keylinePx stage px
  // on screen, whatever the rotation. Inverse of scr = R·p with p in stage px: a unit screen step
  // is (ca, −sa) / (sa, ca) in quad-local stage px, mapped to uv per component.
  vUvKx = vec2(ca * uvH.x / halfPx.x, -sa * uvH.y / halfPx.y);
  vUvKy = vec2(sa * uvH.x / halfPx.x, ca * uvH.y / halfPx.y);
}
`;

/** Sprite fragment: one atlas tap (NEAREST bucket atlases or trilinear hi-res sprites), tinted;
 *  rotor adds the 3×3 keyline under the sprite. `CAR_PREMULTIPLY` outputs rgb·a (blending ONE /
 *  ONE_MINUS_SRC_ALPHA), otherwise straight alpha. Exported for the unit tests.
 */
export const CAR_SPRITE_FRAGMENT = /* glsl */ `
precision highp float;
uniform sampler2D uAtlas;
uniform vec3 uKeylineRgb;
uniform float uKeylineAlpha;
uniform float uKeylinePx;
varying vec2 vUv;
varying vec4 vTint;
varying vec2 vUvKx;
varying vec2 vUvKy;
void main() {
  vec4 s = texture(uAtlas, vUv);
  vec3 rgb = s.rgb * vTint.rgb;
  float a = s.a * vTint.a;
#ifdef CAR_KEYLINE
  float m = s.a;
  m = max(m, texture(uAtlas, vUv + vUvKx * uKeylinePx).a);
  m = max(m, texture(uAtlas, vUv - vUvKx * uKeylinePx).a);
  m = max(m, texture(uAtlas, vUv + vUvKy * uKeylinePx).a);
  m = max(m, texture(uAtlas, vUv - vUvKy * uKeylinePx).a);
  m = max(m, texture(uAtlas, vUv + (vUvKx + vUvKy) * uKeylinePx).a);
  m = max(m, texture(uAtlas, vUv - (vUvKx + vUvKy) * uKeylinePx).a);
  m = max(m, texture(uAtlas, vUv + (vUvKx - vUvKy) * uKeylinePx).a);
  m = max(m, texture(uAtlas, vUv - (vUvKx - vUvKy) * uKeylinePx).a);
  float ka = uKeylineAlpha * (1.0 - a) * step(1.0 / 255.0, m);
  vec3 pa = rgb * a + uKeylineRgb * ka;
  float oa = a + ka;
#ifdef CAR_PREMULTIPLY
  gl_FragColor = vec4(pa, oa);
#else
  gl_FragColor = vec4(oa > 0.0 ? pa / oa : rgb, oa);
#endif
#else
#ifdef CAR_PREMULTIPLY
  gl_FragColor = vec4(rgb * a, a);
#else
  gl_FragColor = vec4(rgb, a);
#endif
#endif
}
`;

export interface CarLayerOptions {
  /** max instances (default 24) */
  capacity?: number;
  /** disc: disc diameter in stage px (default 10); sprite modes: quad **height** in stage px
   *  (width = height · cell aspect) */
  sizePx?: number;
  outlinePx?: number;
  /** core dot diameter in stage px (0 = none); disc mode only */
  corePx?: number;
  outline?: RGB;
  core?: RGB;
  layerMask?: number;
  renderingGroupId?: number;
  /** disc: snap instance centres to stage-px cell centres (default false); sprite modes: snap the
   *  sprite **box origin** to whole stage px (v6 `blit_sprite`, risk 9; default true) */
  pixelSnap?: boolean;
  /** built-in shader mode (default 'disc', unchanged):
   *  - `'atlas'`: non-rotating atlas sprites — per-instance cell (`setCell`) → `uvRect` from
   *    `atlasCells`, tint/fade via `setTint`; heading-bucket map sprites and the non-rotating SC
   *    text quads (corner names) are both this mode;
   *  - `'rotor'`: continuous rotation of one hi-res sprite cell (`setAngle`) with trilinear mip
   *    sampling and a 3×3 shader keyline (zoom-in path).
   *  Requires `atlasTexture` + `atlasCells`; cannot be combined with `material`. */
  mode?: CarLayerMode;
  /** sprite modes: the style's sprite atlas (must be ready: `getSize()` valid; NEAREST sampling
   *  mode for bucket/text atlases, trilinear + mips for the rotor's hi-res sprite). Style-owned —
   *  the layer never disposes it. */
  atlasTexture?: BaseTexture;
  /** sprite modes: the atlas size in texels. Default `atlasTexture.getSize()` — pass it when the
   *  layer is built before the atlas has loaded (cell uv rects and the aspect come from the cell
   *  table × this size, so a wrong size silently skews the quads). */
  atlasSize?: { readonly width: number; readonly height: number };
  /** sprite modes: cell table as uv rects (row = cell index); the quad's aspect = the cell's texel
   *  aspect (uv rect × atlas size) */
  atlasCells?: readonly CarAtlasCell[];
  /** sprite modes: output premultiplied rgb (rgb·a, blending ONE / ONE_MINUS_SRC_ALPHA — the v6
   *  map-sprite "premultiply in shader"); default false (straight alpha, ALPHA_COMBINE) */
  premultiply?: boolean;
  /** rotor: keyline colour (default [14, 15, 18] = #0E0F12) */
  keyline?: RGB;
  /** rotor: keyline alpha (default 0.88; drawn at α·(1 − sprite alpha)) */
  keylineAlpha?: number;
  /** rotor: keyline distance in stage px (default 1) */
  keylinePx?: number;
  /** style-supplied `ShaderMaterial`, used instead of the built-in disc shader (03 §10). It owns its
   *  blending and its INSTANCES path (world0..world3, plus any `extra` attributes by name; the
   *  built-in `uOutlinePx`/`uCorePx`/`uPixelSnap` setters are inert unless the shader declares
   *  them). The layer registers it with the UniformHub (uPxScale, uViewportPx, … when declared) and
   *  disposes it with the mesh. Not combinable with `mode`. */
  material?: ShaderMaterial;
  /** extra per-instance float attributes (03 §10), e.g. a token shader's kind/frac/radii. One
   *  instanced vertex buffer per name (stride floats per instance, attribute name = the shader
   *  attribute name), written per car via `extraRow(name)`; `setCars()` permutes the rows into draw
   *  order and uploads them. Fixed capacity × stride, allocated once — per-frame updates and
   *  uploads never allocate. */
  extra?: readonly CarExtraAttr[];
}

/** a named per-instance float attribute (fixed stride in floats) */
export interface CarExtraAttr {
  readonly name: string;
  readonly stride: number;
}

/** built-in shader mode (03 §10 gap 7): 'disc' (unchanged default), 'atlas' or 'rotor' */
export type CarLayerMode = 'disc' | 'atlas' | 'rotor';

/** one atlas cell as a uv rect (a row of the style's cell table). The quad's top row samples
 *  `v1` and its bottom row `v0`, so `v` is the texture's GL coordinate: a cell whose art spans
 *  image pixels x..x+w, y..y+h (y down from the image top, like `undercut_sprites.json`) maps
 *  to, with W/H the atlas size in texels:
 *  - `new Texture(url)` — Babylon default `invertY: true`, image top = v 1:
 *    `u0 = x/W`, `u1 = (x+w)/W`, `v0 = 1 − (y+h)/H` (bottom), `v1 = 1 − y/H` (top);
 *  - loaded with `invertY: false` (or a canvas upload via `update(false)`, image top = v 0):
 *    `v0 = (y+h)/H`, `v1 = y/H`.
 *  `u` never flips (the atlas art is not mirrored). */
export interface CarAtlasCell {
  readonly u0: number;
  readonly v0: number;
  readonly u1: number;
  readonly v1: number;
}

/** CPU mirror of the sprite vertex shader's stage-px origin snap (the GLSL, `CAR_SPRITE_SNAP_GLSL`,
 *  is the source of truth): snaps the sprite box origin to whole stage px (`n` = max(1,
 *  round(pxScale)) backbuffer px each) and returns the snapped **centre** in NDC. Round-half-up
 *  (`floor(x + 0.5)`), like every stage-px snap in the core; at a whole-number origin the v6
 *  `round(px − 11.5)` differs only at exact .5 boundaries (inside the 3 px parity mask).
 *  Unit-tested at u = 1 and u = 2 in gl-cars-atlas.test.ts; the browser check is Playwright (not
 *  run here). */
export function snapSpriteNdc(
  ndcX: number,
  ndcY: number,
  halfWStage: number,
  halfHStage: number,
  pxScale: number,
  viewportW: number,
  viewportH: number,
  originX: number,
  originY: number,
  backbufferH: number,
): { x: number; y: number } {
  const n = Math.max(1, Math.floor(pxScale + 0.5));
  const p0x = (ndcX * 0.5 + 0.5) * viewportW + originX;
  const p0y = (ndcY * 0.5 + 0.5) * viewportH + originY;
  const spX = p0x;
  const spY = backbufferH - p0y; // y down
  const halfBbX = halfWStage * pxScale;
  const halfBbY = halfHStage * pxScale;
  const orgX = Math.floor((spX - halfBbX) / n + 0.5) * n;
  const orgY = Math.floor((spY - halfBbY) / n + 0.5) * n;
  const cX = orgX + halfBbX;
  const cYdown = orgY + halfBbY;
  const outX = cX - originX;
  const outY = backbufferH - cYdown - originY; // y up, viewport-relative
  return { x: (outX / viewportW) * 2 - 1, y: (outY / viewportH) * 2 - 1 };
}

export interface CarHost {
  scene: Scene;
  hub: UniformHub;
}

let carSeq = 0;

export class CarLayer {
  readonly capacity: number;
  readonly mesh: Mesh;
  readonly material: ShaderMaterial;
  /** resolved built-in mode ('disc' unless created with mode 'atlas'/'rotor'; a style-supplied
   *  material always reports 'disc' — it owns its own shader) */
  readonly mode: CarLayerMode;
  /** resolved pixelSnap ('atlas' defaults on — box-origin stage-px snap, risk 9; 'rotor' defaults
   *  off — continuous rotation at fractional sizes; disc default false). Mutable via setPixelSnap. */
  pixelSnap: boolean;
  private readonly data: Float32Array;
  /** per car (by car index): fill rgba, outline rgba, core rgba, size, visible */
  private readonly fill: Float32Array;
  private readonly outline: Float32Array;
  private readonly core: Float32Array;
  private readonly size: Float32Array;
  private readonly vis: Uint8Array;
  /** sprite modes: per-car atlas cell index, rotation (radians) and tint rgba (0..1) */
  private readonly cells: Int32Array;
  private readonly angles: Float32Array;
  private readonly tint: Float32Array;
  private readonly atlasCells: readonly CarAtlasCell[];
  private readonly atlasW: number;
  private readonly atlasH: number;
  /** extra per-instance attributes: per-car rows + per-slot upload buffers (fixed, never reallocated) */
  private readonly extras: readonly { name: string; stride: number; rows: Float32Array; slots: Float32Array }[];
  private count = 0;

  constructor(host: CarHost, opts: CarLayerOptions = {}) {
    const mode = opts.mode ?? 'disc';
    if (mode !== 'disc' && opts.material) {
      throw new Error('CarLayer: options.material replaces the built-in shaders; drop mode');
    }
    if (mode !== 'disc' && (!opts.atlasTexture || !opts.atlasCells || opts.atlasCells.length === 0)) {
      throw new Error(`CarLayer mode '${mode}' needs atlasTexture and atlasCells`);
    }
    this.mode = mode;
    this.pixelSnap = opts.pixelSnap ?? mode === 'atlas'; // rotor stays unsnapped (zoom-in path, D-4)
    this.atlasCells = opts.atlasCells ?? [];
    const sz = mode !== 'disc' ? (opts.atlasSize ?? opts.atlasTexture?.getSize()) : undefined;
    this.atlasW = sz && sz.width > 0 ? sz.width : 1;
    this.atlasH = sz && sz.height > 0 ? sz.height : 1;
    const cap = (this.capacity = opts.capacity ?? 24);
    const id = carSeq++;
    this.mesh = new Mesh(`cars${String(id)}`, host.scene);
    const vd = new VertexData();
    vd.positions = [-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0];
    vd.indices = [0, 1, 2, 0, 2, 3];
    vd.applyToMesh(this.mesh, false);
    this.mesh.isPickable = false;
    this.mesh.alwaysSelectAsActiveMesh = true;
    this.mesh.doNotSyncBoundingInfo = true;
    this.mesh.layerMask = opts.layerMask ?? LAYER.MAIN;
    this.mesh.renderingGroupId = opts.renderingGroupId ?? GROUP.CARS;
    this.data = new Float32Array(cap * 16);
    this.fill = new Float32Array(cap * 4).fill(1);
    this.outline = new Float32Array(cap * 4);
    this.core = new Float32Array(cap * 4);
    this.size = new Float32Array(cap).fill(opts.sizePx ?? 10);
    this.vis = new Uint8Array(cap).fill(1);
    this.cells = new Int32Array(cap);
    this.angles = new Float32Array(cap);
    this.tint = new Float32Array(cap * 4).fill(1);
    const o = opts.outline ?? [0, 0, 0];
    const c = opts.core ?? [255, 255, 255];
    for (let i = 0; i < cap; i++) {
      this.outline.set([o[0] / 255, o[1] / 255, o[2] / 255, 1], i * 4);
      this.core.set([c[0] / 255, c[1] / 255, c[2] / 255, opts.corePx ? 1 : 0], i * 4);
    }
    this.mesh.thinInstanceSetBuffer('matrix', this.data, 16, false);
    this.mesh.thinInstanceCount = 0;
    this.mesh.refreshBoundingInfo(); // transparent sorting reads the sub-mesh bounding sphere
    this.extras = (opts.extra ?? []).map((a) => ({
      name: a.name,
      stride: a.stride,
      rows: new Float32Array(cap * a.stride),
      slots: new Float32Array(cap * a.stride),
    }));
    for (const e of this.extras) this.mesh.thinInstanceSetBuffer(e.name, e.slots, e.stride, false);
    if (opts.material) {
      this.material = opts.material;
    } else if (mode === 'disc') {
      this.material = new ShaderMaterial(
        `cars${String(id)}`,
        host.scene,
        { vertexSource: CAR_VERTEX, fragmentSource: CAR_FRAGMENT },
        {
          attributes: ['position'],
          uniforms: ['viewProjection', 'uOutlinePx', 'uCorePx', 'uPixelSnap', ...HUB_UNIFORMS],
          needAlphaBlending: true,
        },
      );
      this.material.backFaceCulling = false;
      this.material.alphaMode = Constants.ALPHA_COMBINE;
      this.material.disableDepthWrite = true;
      this.material.setFloat('uOutlinePx', opts.outlinePx ?? 1.5);
      this.material.setFloat('uCorePx', opts.corePx ?? 0);
      this.material.setFloat('uPixelSnap', opts.pixelSnap ? 1 : 0);
    } else {
      const defines = mode === 'rotor' ? ['CAR_KEYLINE'] : [];
      if (opts.premultiply) defines.push('CAR_PREMULTIPLY');
      this.material = new ShaderMaterial(
        `cars${String(id)}`,
        host.scene,
        { vertexSource: CAR_SPRITE_VERTEX, fragmentSource: CAR_SPRITE_FRAGMENT },
        {
          attributes: ['position'],
          uniforms: [
            'viewProjection',
            'uPixelSnap',
            ...(mode === 'rotor' ? ['uKeylineRgb', 'uKeylineAlpha', 'uKeylinePx'] : []),
            ...HUB_UNIFORMS,
          ],
          samplers: ['uAtlas'],
          needAlphaBlending: true,
          defines,
        },
      );
      this.material.backFaceCulling = false;
      this.material.alphaMode = opts.premultiply ? Constants.ALPHA_PREMULTIPLIED : Constants.ALPHA_COMBINE;
      this.material.disableDepthWrite = true;
      this.material.setTexture('uAtlas', (opts.atlasTexture as BaseTexture));
      this.material.setFloat('uPixelSnap', this.pixelSnap ? 1 : 0);
      if (mode === 'rotor') {
        const k = opts.keyline ?? [14, 15, 18];
        this.material.setColor3('uKeylineRgb', new Color3(k[0] / 255, k[1] / 255, k[2] / 255));
        this.material.setFloat('uKeylineAlpha', opts.keylineAlpha ?? 0.88);
        this.material.setFloat('uKeylinePx', opts.keylinePx ?? 1);
      }
    }
    host.hub.register(this.material);
    this.mesh.material = this.material;
  }

  setOutlinePx(px: number): void {
    this.material.setFloat('uOutlinePx', px);
  }
  setCorePx(px: number): void {
    this.material.setFloat('uCorePx', px);
  }
  setPixelSnap(on: boolean): void {
    this.pixelSnap = on;
    this.material.setFloat('uPixelSnap', on ? 1 : 0);
  }

  /** Fill colour of one car (0..255, alpha 0..1). */
  setColor(car: number, r: number, g: number, b: number, a = 1): void {
    const i = car * 4;
    this.fill[i] = r / 255;
    this.fill[i + 1] = g / 255;
    this.fill[i + 2] = b / 255;
    this.fill[i + 3] = a;
  }
  setOutline(car: number, r: number, g: number, b: number, a = 1): void {
    const i = car * 4;
    this.outline[i] = r / 255;
    this.outline[i + 1] = g / 255;
    this.outline[i + 2] = b / 255;
    this.outline[i + 3] = a;
  }
  /** Core dot colour (alpha 0 hides the core for this car, e.g. teammate markers). */
  setCore(car: number, r: number, g: number, b: number, a = 1): void {
    const i = car * 4;
    this.core[i] = r / 255;
    this.core[i + 1] = g / 255;
    this.core[i + 2] = b / 255;
    this.core[i + 3] = a;
  }
  setSize(car: number, px: number): void {
    this.size[car] = px;
  }
  setVisible(car: number, on: boolean): void {
    this.vis[car] = on ? 1 : 0;
  }

  /** Atlas cell index (row into `atlasCells`) for one car (atlas/rotor modes; default 0). */
  setCell(car: number, index: number): void {
    this.cells[car] = index;
  }
  /** Rotation for one car in radians (rotor mode; default 0 = atlas upright; positive = CCW in the
   *  GL y-up frame — v6 screen angles map via `headingToScreen`). */
  setAngle(car: number, radians: number): void {
    this.angles[car] = radians;
  }
  /** Tint multiplier for one car (atlas/rotor modes; default white): rgb 0..255, alpha 0..1
   *  (finished fade: setTint(car, 255, 255, 255, 0.55)). */
  setTint(car: number, r: number, g: number, b: number, a = 1): void {
    const i = car * 4;
    this.tint[i] = r / 255;
    this.tint[i + 1] = g / 255;
    this.tint[i + 2] = b / 255;
    this.tint[i + 3] = a;
  }

  /**
   * Upload positions for `n` cars (world metres, indexed by car). `order` lists car indices back
   * to front (draw order; later = on top); default 0..n-1. Allocation-free. Packs per mode: disc =
   * (x, y, sizePx, visible / fill / outline / core); atlas/rotor = (x, y, sizePx, visible) +
   * (angle, tint rgb) + (tint a, cell uv0, uv1.x) + (uv1.y, cell aspect).
   */
  setCars(x: ArrayLike<number>, y: ArrayLike<number>, n: number, order?: ArrayLike<number> | null): void {
    const m = Math.min(n, this.capacity);
    const d = this.data;
    const sprite = this.mode !== 'disc';
    for (let k = 0; k < m; k++) {
      const car = order ? (order[k] ?? k) : k;
      const o = k * 16;
      d[o] = x[car] ?? 0;
      d[o + 1] = y[car] ?? 0;
      d[o + 2] = this.size[car] ?? 10;
      d[o + 3] = this.vis[car] ?? 0;
      if (sprite) {
        const ci = Math.min(Math.max(this.cells[car] ?? 0, 0), this.atlasCells.length - 1);
        const cell = this.atlasCells[ci] ?? { u0: 0, v0: 0, u1: 1, v1: 1 };
        const t4 = car * 4;
        d[o + 4] = this.angles[car] ?? 0;
        d[o + 5] = this.tint[t4] ?? 1;
        d[o + 6] = this.tint[t4 + 1] ?? 1;
        d[o + 7] = this.tint[t4 + 2] ?? 1;
        d[o + 8] = this.tint[t4 + 3] ?? 1;
        d[o + 9] = cell.u0;
        d[o + 10] = cell.v0;
        d[o + 11] = cell.u1;
        d[o + 12] = cell.v1;
        const du = (cell.u1 - cell.u0) * this.atlasW;
        const dv = (cell.v1 - cell.v0) * this.atlasH;
        d[o + 13] = dv > 0 ? du / dv : 1;
      } else {
        const c4 = car * 4;
        for (let j = 0; j < 4; j++) {
          d[o + 4 + j] = this.fill[c4 + j] ?? 1;
          d[o + 8 + j] = this.outline[c4 + j] ?? 0;
          d[o + 12 + j] = this.core[c4 + j] ?? 0;
        }
      }
    }
    this.count = m;
    this.mesh.thinInstanceCount = m;
    this.mesh.thinInstanceBufferUpdated('matrix');
    if (this.extras.length > 0) {
      for (let k = 0; k < m; k++) {
        const car = order ? (order[k] ?? k) : k;
        for (const e of this.extras) {
          const s = e.stride;
          const src = car * s;
          const dst = k * s;
          for (let j = 0; j < s; j++) e.slots[dst + j] = e.rows[src + j] ?? 0;
        }
      }
      for (const e of this.extras) this.mesh.thinInstanceBufferUpdated(e.name);
    }
  }

  /**
   * Row buffer (capacity × stride, indexed by car) of an extra per-instance attribute: write the
   * values for the cars that will be drawn, then call `setCars()` — it permutes the rows into draw
   * order, uploads them (`thinInstanceBufferUpdated`) and allocates nothing. Returns null when the
   * layer was not created with an attribute of that name.
   */
  extraRow(name: string): Float32Array | null {
    return this.extras.find((e) => e.name === name)?.rows ?? null;
  }

  get instanceCount(): number {
    return this.count;
  }

  setEnabled(on: boolean): void {
    this.mesh.setEnabled(on);
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose(); // style-owned atlas textures are never disposed here
  }
}
